
import { Link } from 'react-router-dom'

import {
  FindUsOnSocials,
  GlobalFooter
} from '@/components/global'

export interface ServicePageConfig {
  // Meta information
  title: string
  description: string
  slug: string

  // Hero section
  heroImage: string
  serviceName: string

  // Why FGA Prestige Section C (Image left, Content right)
  whySectionC: {
    title: string
    content: string
    image: string
    imageAlt: string
  }

  // Why FGA Prestige Section A (Content left, Image right) - optional
  whySectionA?: {
    title: string
    content: string
    image: string
    imageAlt: string
  }

  // Why FGA Prestige Section B (Content left, Image right)
  whySectionB: {
    title: string
    benefits: Array<{
      title: string
      description: string
    }>
    image: string
    imageAlt: string
  }

  // Masterpiece section background image
  masterpieceBackgroundImage: string
}

// Generic Hero Section Component
const ServiceHeroSection = ({ heroImage, serviceName, slug }: { heroImage: string, serviceName: string, slug: string }) => {
  return (
    <section
      style={{
        minHeight: '560px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        width: '100%'
      }}
    >
      {/* Image Column */}
      <div
        style={{
          minHeight: '400px'
        }}
      >
        <img
          src={heroImage}
          alt={serviceName}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </div>

      {/* Content Column */}
      <div
        style={{
          backgroundColor: '#EFEFEF',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '64px 32px',
          minHeight: '400px'
        }}
      >
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            textAlign: 'center'
          }}
        >
          {/* Subheading */}
          <h2
            style={{
              fontFamily: 'Cardo, serif',
              fontSize: '24px',
              fontStyle: 'italic',
              color: '#764F2B',
              margin: 0,
              lineHeight: 1.2
            }}
          >
            {slug.includes('bespoke-shopping') ? 'Bespoke Shopping' : 'Our Services'}
          </h2>

          {/* Main Title */}
          <h1
            style={{
              fontFamily: 'Cardo, serif',
              fontSize: 'clamp(32px, 8vw, 66px)',
              fontWeight: 'normal',
              color: '#000000',
              margin: 0,
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}
          >
            {serviceName}
          </h1>
        </div>
      </div>
    </section>
  )
}

// Generic Breadcrumb Component
const ServiceBreadcrumb = ({ serviceName, slug }: { serviceName: string, slug: string }) => {
  const isBespokeShopping = slug.includes('bespoke-shopping')
  
  return (
    <section
      className="px-6 py-8 md:px-32 md:py-8"
      style={{
        backgroundColor: '#FFFFFF'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: '14px',
          color: '#7B7B7B'
        }}
      >
        <Link to="/" style={{ color: '#7B7B7B', textDecoration: 'none' }}>
          <span>Home</span>
        </Link>
        <span>/</span>
        {isBespokeShopping ? (
          <Link to="/bespoke-shopping" style={{ color: '#7B7B7B', textDecoration: 'none' }}>
            <span>Bespoke Shopping</span>
          </Link>
        ) : (
          <Link to="/member-services" style={{ color: '#7B7B7B', textDecoration: 'none' }}>
            <span>Member Services</span>
          </Link>
        )}
        <span>/</span>
        <span style={{ color: '#000000' }}>{serviceName}</span>
      </div>
    </section>
  )
}

// Generic Intro Section Component
const ServiceIntroSection = ({ title, content }: { title: string, content: string }) => {
  return (
    <section
      className="px-6 md:px-32"
      style={{
        backgroundColor: '#FFFFFF'
      }}
    >
      <div
        style={{
          textAlign: 'left'
        }}
      >
        <h2
          style={{
            fontFamily: 'Cardo, serif',
            fontSize: '48px',
            fontWeight: 'normal',
            color: '#000000',
            margin: '0 0 40px 0',
            lineHeight: 1.2,
            borderBottom: '0.5px solid #000000',
            paddingBottom: '8px'
          }}
        >
          {title}
        </h2>
        
        <p
          style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: '16px',
            color: '#7B7B7B',
            lineHeight: 1.6,
            marginTop: '40px'
          }}
        >
          {content}
        </p>
      </div>
    </section>
  )
}

// Generic Body Content Section Component
const ServiceBodyContentSection = ({
  bodyImages,
  bodyImageCaption,
  bodyParagraphs
}: {
  bodyImages: { image1: string, image2: string, image3?: string, image4?: string },
  bodyImageCaption: string,
  bodyParagraphs?: string[]
}) => {
  return (
    <section
      style={{
        backgroundColor: '#FFFFFF',
        paddingTop: '64px',
        paddingBottom: '64px',
      }}
      className="pt-0 md:pt-16"
    >
      {/* Row 1 - Image Section: Two square images with text below */}
      <div
        className="px-6 md:px-32"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start'
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            width: '100%'
          }}
        >
          <div
            style={{
              aspectRatio: '1',
              backgroundImage: `url("${bodyImages.image1}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div
            style={{
              aspectRatio: '1',
              backgroundImage: `url("${bodyImages.image2}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          {bodyImages.image3 && (
            <div
              style={{
                aspectRatio: '1',
                backgroundImage: `url("${bodyImages.image3}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          )}
          {bodyImages.image4 && (
            <div
              style={{
                aspectRatio: '1',
                backgroundImage: `url("${bodyImages.image4}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          )}
        </div>
        <p
          style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: '16px',
            color: '#7B7B7B',
            textAlign: 'left'
          }}
        >
          {bodyImageCaption}
        </p>
      </div>

      {/* Row 2 - Paragraph Section - Only render if bodyParagraphs exist */}
      {bodyParagraphs && bodyParagraphs.length > 0 && (
        <div
          className="px-6 md:px-32 py-6 md:py-0"
          style={{
            backgroundColor: '#FFFFFF'
          }}
        >
          <div
            style={{
              padding: '24px 0px',
              width: '100%'
            }}
            className="!p-0 md:!py-6"
          >
            {bodyParagraphs.map((paragraph, index) => (
              <p
                key={index}
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '16px',
                  color: '#7B7B7B',
                  lineHeight: 1.6,
                  marginBottom: index < bodyParagraphs.length - 1 ? '24px' : '0'
                }}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

// Configurable Why FGA Prestige Section C (Image left, Content right)
const ConfigurableWhyFGAPrestigeSectionC = ({ 
  title, 
  content, 
  image, 
  imageAlt 
}: { 
  title: string, 
  content: string, 
  image: string, 
  imageAlt: string 
}) => {
  return (
    <section
      style={{ backgroundColor: '#F7F8FA' }}
    >
      <div
        className="px-6 md:px-32 pb-16"
        style={{
          backgroundColor: '#FFFFFF'
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full m-0">
          {/* Image Column - First */}
          <div
            className="flex items-stretch w-full h-full"
          >
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-full min-h-[400px] md:min-h-[560px] object-cover rounded-none block"
            />
          </div>

          {/* Content Column - Second */}
          <div
            className="flex flex-col justify-center py-6 px-0 md:px-20 md:py-20"
          >
            <div style={{ width: '100%' }}>
              {title && (
                <h2
                  style={{
                    fontFamily: 'Cardo, serif',
                    fontSize: '40px',
                    color: '#111',
                    fontWeight: '400',
                    marginBottom: '32px',
                    lineHeight: '1.2'
                  }}
                >
                  {title}
                </h2>
              )}
              <div
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: '#7B7B7B'
                }}
              >
                <div style={{ marginBottom: '24px' }}>
                  <p style={{ marginBottom: '16px' }}>
                    {content}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  )
}

// Configurable Why FGA Prestige Section A (Content left, Image right)
const ConfigurableWhyFGAPrestigeSectionA = ({
  title,
  content,
  image,
  imageAlt
}: {
  title: string,
  content: string,
  image: string,
  imageAlt: string
}) => {
  return (
    <section
      style={{ backgroundColor: '#F7F8FA' }}
    >
      <div
        className="px-6 md:px-32 pb-16"
        style={{
          backgroundColor: '#FFFFFF'
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full m-0">
          {/* Content Column - First */}
          <div
            className="flex flex-col justify-center py-6 px-0 md:px-20 md:py-20 order-2 md:order-1"
          >
            <div style={{ width: '100%' }}>
              {title && (
                <h2
                  style={{
                    fontFamily: 'Cardo, serif',
                    fontSize: '40px',
                    color: '#111',
                    fontWeight: '400',
                    marginBottom: '32px',
                    lineHeight: '1.2'
                  }}
                >
                  {title}
                </h2>
              )}
              <div
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: '#7B7B7B'
                }}
              >
                {content && content.split('\n\n').map((para, index) => (
                  <p key={index} style={{ marginBottom: '16px' }}>
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>

          {/* Image Column - Second */}
          <div
            className="flex items-stretch w-full h-full order-1 md:order-2"
          >
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-full min-h-[400px] md:min-h-[560px] object-cover rounded-none block"
            />
          </div>
        </div>
      </div>

    </section>
  )
}

// Configurable Why FGA Prestige Section B (Content left, Image right)
const ConfigurableWhyFGAPrestigeSectionB = ({
  title, 
  benefits, 
  image, 
  imageAlt 
}: { 
  title: string, 
  benefits: Array<{ title: string, description: string }>, 
  image: string, 
  imageAlt: string 
}) => {
  return (
    <section
      style={{ backgroundColor: '#F7F8FA' }}
    >
      <div
        className="px-6 md:px-32 pb-16"
        style={{
          backgroundColor: '#FFFFFF'
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full m-0">
          {/* Content Column - First */}
          <div
            className="flex flex-col justify-center py-6 px-0 md:px-20 md:py-20 order-2 md:order-1"
          >
            <div style={{ width: '100%' }}>
              <h2
                style={{
                  fontFamily: 'Cardo, serif',
                  fontSize: '40px',
                  color: '#111',
                  fontWeight: '400',
                  marginBottom: '32px',
                  lineHeight: '1.2'
                }}
              >
                {title}
              </h2>
              <div
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: '#7B7B7B'
                }}
              >
                {benefits.map((benefit, index) => (
                  <div key={index} style={{ marginBottom: index < benefits.length - 1 ? '24px' : '0' }}>
                    <h3 style={{ 
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#764F2B',
                      marginBottom: '12px'
                    }}>
                      {benefit.title}
                    </h3>
                    <p style={{ marginBottom: '16px' }}>
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Image Column - Second */}
          <div
            className="flex items-stretch w-full h-full order-1 md:order-2"
          >
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-full min-h-[400px] md:min-h-[560px] object-cover rounded-none block"
            />
          </div>
        </div>
      </div>

    </section>
  )
}

// Configurable Masterpiece Section Component
const ConfigurableMasterpieceSection = ({ backgroundImage =  "/Member Services/Your Life Our Masterpiece/bg.png" }: { backgroundImage?: string }) => {
  // backgroundImage = "/Member Services/Your Life Our Masterpiece/bg.png";

  return (
    <section
      className="w-full relative px-6 md:px-32"
      style={{
        height: '484px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <img
        src={backgroundImage}
        alt="Your Life, Our Masterpiece"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -1
        }}
      />
      <div className="w-full text-center">
        <h2
          style={{
            fontFamily: 'Cardo, serif',
            fontSize: '32px',
            color: '#FFFFFF',
            fontWeight: '400',
            marginBottom: '32px',
            textAlign: 'center'
          }}
        >
          Your Life, Our Masterpiece.
        </h2>
        
        <p
          style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: '16px',
            color: '#FFFFFF',
            lineHeight: '1.6',
            marginBottom: '32px',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 32px auto'
          }}
        >
          Having pioneered and perfected the concept of luxury lifestyle management, we know one thing for certain: it's all about you. We've provided the world's most distinguished individuals access to industry-leading private concierge services – and now, it's your turn. If you want the best in life, come to the best in the business.
        </p>
        
        <Link
          to="/membership-enquiry"
          style={{
            backgroundColor: '#FFFFFF',
            color: '#000000',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: '16px',
            padding: '21px 36px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            textDecoration: 'none',
            display: 'inline-block'
          }}
        >
          Become a Member
        </Link>
      </div>
    </section>
  )
}

// Main Template Component
export default function ServicePageTemplate({ config }: { config: ServicePageConfig }) {
  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <ServiceHeroSection
        heroImage={config.heroImage}
        serviceName={config.serviceName}
        slug={config.slug}
      />
      
      {/* Breadcrumb */}
      <ServiceBreadcrumb serviceName={config.serviceName} slug={config.slug} />
      
      {/* Why FGA Prestige Section A - Configurable Version - Conditional */}
      {config.whySectionA && (
        <ConfigurableWhyFGAPrestigeSectionA
          title={config.whySectionA.title}
          content={config.whySectionA.content}
          image={config.whySectionA.image}
          imageAlt={config.whySectionA.imageAlt}
        />
      )}

      {/* Why FGA Prestige Section C - Configurable Version */}
      <ConfigurableWhyFGAPrestigeSectionC
        title={config.whySectionC.title}
        content={config.whySectionC.content}
        image={config.whySectionC.image}
        imageAlt={config.whySectionC.imageAlt}
      />

      {/* Why FGA Prestige Section B - Configurable Version */}
      <ConfigurableWhyFGAPrestigeSectionB
        title={config.whySectionB.title}
        benefits={config.whySectionB.benefits}
        image={config.whySectionB.image}
        imageAlt={config.whySectionB.imageAlt}
      />
      
      {/* Your Life, Our Masterpiece - configurable background image */}
      <ConfigurableMasterpieceSection
        backgroundImage={config.masterpieceBackgroundImage}
      />
      
    </main>
  )
}